import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View, Switch, Pressable, Image } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { Container, I18n, Navigation, Panel, ErrorView } from '../../components';
import { __WEB__, COLOR, FONT, THEME } from '../../../constants/styles';
import { getCurrentEmployer } from '../../../selectors/employers';
import { getAppointmentSlotAvailabilityAuthed, setSelectedEventType } from './slice';
import { deleteEvent, fetchEventsForMonth } from '../../calendar/actions';
import { openCaseInfo } from '../../cases/actions';
import moment from 'moment';
import { getApptEventsForCalendarDisplay, getApptOverrides } from '../selector';
import { getAdminSchedulerEventTypesPermissions } from '../../../selectors/scheduler';
import Cal from './cal_admin';
import { appointmentLabelForType } from './common_views';
import CancelledAppointments from './cancelled_appointments';
import ScheduledAppointments from './scheduled_appointments';
import RequestedAppointments from './requested_appointments';
import {
  EVENT_TYPE_READINESS,
  EVENT_TYPES_REQUIRING_APPROVAL,
  EVENT_TYPE_CMR,
  EVENT_TYPE_CMR_FLY,
} from '../../../constants/eventTypes';
import { CLOUD_PROVIDER, env } from '../../../constants/env';
import { REQUESTING } from '../../../actions/statusTypes';
import { getCMRRevenueCycleServices } from '../cmr_revenue_cycle/slice';

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

export const isCMREventType = (eventType) => {
  return [EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(eventType);
};

const CMRSwitch = memo(({ isFlyer, onToggle }) => {
  return (
    <View style={styles.cmrSwitchRow}>
      <Text
        style={[
          isFlyer ? THEME.textRegular : THEME.textSemibold,
          !__WEB__ ? styles.cmrMobileLabel : null,
          styles.cmrLeftLabel,
        ]}
      >
        {__WEB__ ? 'Non-Fly CMR' : 'Non-Fly'}
      </Text>
      <Switch
        value={isFlyer}
        onValueChange={onToggle}
        thumbColor={'#FFF'}
        activeThumbColor={'#FFF'}
        trackColor={'#939393'}
        activeTrackColor={'#939393'}
      />
      <Text
        style={[
          !isFlyer ? THEME.textRegular : THEME.textSemibold,
          !__WEB__ ? styles.cmrMobileLabel : null,
          styles.cmrRightLabel,
        ]}
      >
        {__WEB__ ? 'Fly CMR' : 'Fly'}
      </Text>
    </View>
  );
});

const HeaderActions = memo(({ showExportButton, showAgentDemoButton, onExport, onAgentDemo }) => {
  if (!showExportButton && !showAgentDemoButton) return null;

  return (
    <View style={styles.headerActions}>
      {showExportButton && (
        <Pressable style={styles.exportButton} onPress={onExport}>
          <Image
            source={require('../../../../resources/icon-logout.png')}
            style={styles.exportIcon}
          />
          <Text style={styles.exportText}>EXPORT</Text>
        </Pressable>
      )}
      {showAgentDemoButton && (
        <Pressable style={styles.bulkCallButton} onPress={onAgentDemo} testID="bulk-call-button">
          <Text style={styles.bulkCallText}>AGENT DEMO</Text>
        </Pressable>
      )}
    </View>
  );
});

const PHAAdmin = memo((props) => {
  const dispatch = useDispatch();
  const employer = useSelector(getCurrentEmployer);
  const selectedEventType = useSelector((state) => state.screens.scheduler.selectedEventType);
  const aptsRequestStatus = useSelector((state) => state.screens.scheduler.aptsRequestStatus);
  const aptsSlotAvailabilityData = useSelector(
    (state) => state.screens.scheduler.aptsSlotAvailabilityData
  );
  const aptsSlotAvailabilityStatus = useSelector(
    (state) => state.screens.scheduler.aptsSlotAvailabilityStatus
  );
  const deleteAptsSlotOverridesError = useSelector(
    (state) => state.screens.scheduler.deleteAptsSlotOverridesError
  );
  const aptsSlotOverrideError = useSelector(
    (state) => state.screens.scheduler.aptsSlotOverrideError
  );
  const aptsSlotAvailabilityError = useSelector(
    (state) => state.screens.scheduler.aptsSlotAvailabilityError
  );
  const appointmentRequestsFetchError = useSelector(
    (state) => state.screens.scheduler.appointmentRequestsFetchError
  );
  const appointmentRequestsFetchStatus = useSelector(
    (state) => state.screens.scheduler.appointmentRequestsFetchStatus
  );
  const scheduleDataStatus = useSelector((state) => state.screens.scheduler.scheduleDataStatus);
  const aptsSlotOverrideStatus = useSelector(
    (state) => state.screens.scheduler.aptsSlotOverrideStatus
  );
  const scheduleDataError = useSelector((state) => state.screens.scheduler.scheduleDataError);
  const appointmentEventsData = useSelector(getApptEventsForCalendarDisplay);
  const overrides = useSelector(getApptOverrides);
  const appointmentDetails = useSelector((state) => state.screens.scheduler.appointmentDetails);
  const wsConnected = useSelector((state) => state.application.wsConnected);
  const userEmail = useSelector((state) => state.entities.userProfile?.email);
  const permissionsForEventTypes = useSelector(getAdminSchedulerEventTypesPermissions);
  const noPermissionForSelectedEventType = useSelector(
    (state) => state.screens.scheduler.noPermissionForSelectedEventType
  );
  const cmrRevenueCycleServicesError = useSelector(
    (state) => state.screens.cmrRevenueCycle.servicesError
  );

  const employerId = employer?.employerId;
  const isCMRSelectedEvent = isCMREventType(selectedEventType);

  const [selectedDate, setSelectedDate] = useState(null);
  const [monthDate, setMonthDate] = useState(null);
  const [confirmationOpts, setConfirmationOpts] = useState(null);

  useEffect(() => {
    if (props.eventType) {
      dispatch(setSelectedEventType(props.eventType));
    }
  }, [dispatch, props.eventType]);

  useEffect(() => {
    if (!employerId || !isCMRSelectedEvent) return;
    dispatch(getCMRRevenueCycleServices({ employerId }));
  }, [dispatch, employerId, isCMRSelectedEvent]);

  useEffect(() => {
    if (!employerId || !monthDate || !wsConnected || !selectedEventType) return;

    if (selectedEventType === EVENT_TYPE_READINESS) return;

    dispatch(
      getAppointmentSlotAvailabilityAuthed({
        employerId,
        month: monthDate,
        eventType: selectedEventType,
      })
    );
  }, [dispatch, employerId, monthDate, selectedEventType, wsConnected]);

  useEffect(() => {
    if (!monthDate || !wsConnected) return;
    dispatch(fetchEventsForMonth(monthDate));
  }, [dispatch, monthDate, wsConnected]);

  useEffect(() => {
    if (!selectedDate) {
      setMonthDate(null);
      return;
    }

    setMonthDate(moment(selectedDate).startOf('month').format('YYYY-MM-DD'));
  }, [selectedDate]);

  const handleCancelledNotification = useCallback(
    (textNode, eventType) => {
      const notificationOpts = textNode
        ? {
            textNode,
            onPress: () => {
              dispatch(setSelectedEventType(eventType));
            },
          }
        : null;

      props.onNotificationOpts?.(notificationOpts);
    },
    [props.onNotificationOpts]
  );

  const cancelAppointment = useCallback(
    (event) => {
      dispatch(deleteEvent(event.eventId, event.caseId, null, null));
    },
    [dispatch]
  );

  const handleCancelEventPress = useCallback(
    (event) => {
      setConfirmationOpts({
        title: 'Cancel this appointment?',
        description: 'The appointment will be removed from the schedule',
        confirm: 'Yes, Cancel',
        reject: 'Nevermind',
        onConfirmed: () => {
          cancelAppointment(event);
          setConfirmationOpts(null);
        },
        onCancelled: () => setConfirmationOpts(null),
      });
    },
    [cancelAppointment]
  );

  const handleEventInfoPress = useCallback(
    (request, event) => {
      if (!request && event.caseId) {
        dispatch(openCaseInfo(event.caseId));
      }

      Navigation.showModal({
        screen: 'CreatePHARequest',
        title: 'Appointment Info',
        passProps: {
          request,
          readOnly: true,
          admin: true,
          hideButtons: true,
          allDay: !!event.allDay,
          e: event,
        },
      });
    },
    [dispatch]
  );

  const handleScheduledEventContactPress = useCallback(
    (event) => {
      Navigation.showModal({
        screen: 'AppointmentRequestSms',
        title: 'Send Text Message',
        passProps: {
          event,
          employerId,
        },
      });
    },
    [employerId]
  );

  const handleScheduledEventRevenuePress = useCallback(
    (event) => {
      Navigation.showModal({
        screen: 'CMRAttachedServicesForm',
        title: 'CMR Revenue Cycle',
        passProps: {
          employerId,
          eventId: event.eventId,
          patientName: `${event.rank || ''} ${event.caseName || ''}`.trim(),
          appointmentDate: event.startDate || event.origStartDate,
        },
        options: {
          containerStyle: {
            maxWidth: 900,
          },
        },
      });
    },
    [employerId]
  );

  const handleExportPress = useCallback(() => {
    Navigation.showModal({
      screen: 'AppointmentRequestsExportForm',
      title: I18n.ac('export'),
    });
  }, []);

  const handleBulkCallPress = useCallback(() => {
    Navigation.showModal({
      screen: 'BulkCallModal',
      title: 'Agent Demo',
      passProps: {
        employerId,
      },
      options: {
        width: 400,
        height: 350,
      },
    });
  }, [employerId]);

  const handleFlyerToggle = useCallback(() => {
    const newEventType =
      selectedEventType === EVENT_TYPE_CMR_FLY ? EVENT_TYPE_CMR : EVENT_TYPE_CMR_FLY;
    dispatch(setSelectedEventType(newEventType));
  }, [dispatch, selectedEventType]);

  const canEditSelectedEventType = !!permissionsForEventTypes?.[selectedEventType]?.canEdit;

  const hasAppointmentRequests = useMemo(() => {
    return EVENT_TYPES_REQUIRING_APPROVAL.includes(selectedEventType);
  }, [selectedEventType]);

  const isFlyer = selectedEventType === EVENT_TYPE_CMR_FLY;
  const isCMREvent = isCMRSelectedEvent;
  const displayEventType = isCMREvent ? EVENT_TYPE_CMR : selectedEventType;
  const selectedEventLabel = displayEventType
    ? appointmentLabelForType(displayEventType)
    : 'Appointment';
  const eventsByStartDate =
    appointmentEventsData?.[selectedEventType]?.eventsByStartDate || EMPTY_OBJECT;
  const events = appointmentEventsData?.[selectedEventType]?.eventsArrayForCal || EMPTY_ARRAY;
  const eventsByTime = appointmentEventsData?.[selectedEventType]?.eventsByTime || EMPTY_OBJECT;
  const isOutpatientUser = userEmail?.endsWith('@outpatient.ai');
  const showExportButton = isCMREvent && __WEB__ && canEditSelectedEventType;
  const showAgentDemoButton =
    __WEB__ && canEditSelectedEventType && isOutpatientUser && env(CLOUD_PROVIDER) === 'azure';
  const appointmentDataError =
    aptsSlotAvailabilityError ||
    deleteAptsSlotOverridesError ||
    aptsSlotOverrideError ||
    scheduleDataError ||
    appointmentRequestsFetchError;
  const appointmentDataErrorText = appointmentDataError
    ? 'An error has occurred while fetching appointment data. Please refresh your browser or try again later. If the problem persists, please contact support. \n\n' +
      appointmentDataError.message
    : null;
  const cmrRevenueCycleServicesErrorText =
    isCMREvent && cmrRevenueCycleServicesError
      ? 'An error has occurred while fetching CMR revenue cycle services. CMR revenue cycle features may be unavailable. Please refresh your browser or try again later. If the problem persists, please contact support. \n\n' +
        cmrRevenueCycleServicesError.message
      : null;
  const dataErrorText = [appointmentDataErrorText, cmrRevenueCycleServicesErrorText]
    .filter(Boolean)
    .join('\n\n');
  const errorText = dataErrorText
    ? dataErrorText
    : noPermissionForSelectedEventType
      ? 'You do not have permission to view this event type.'
      : null;
  const appointmentsTitle = isCMREvent
    ? `${selectedEventType === EVENT_TYPE_CMR ? 'Non-Fly CMR' : 'Fly CMR'} Appointments`
    : `${selectedEventLabel} Appointments`;
  const calendarTitle = isCMREvent ? (
    <CMRSwitch isFlyer={isFlyer} onToggle={handleFlyerToggle} />
  ) : (
    `${selectedEventLabel} Appointment Schedule`
  );
  const todaysDateLabel = moment(selectedDate).format('MMMM DD, YYYY');
  const shouldRenderCancelledAppointments = selectedEventType?.indexOf('Appointment') >= 0;

  return (
    <Container
      scrollView={true}
      style={styles.container}
      renderActivityOverlay={
        appointmentRequestsFetchStatus === REQUESTING ||
        aptsSlotAvailabilityStatus === REQUESTING ||
        scheduleDataStatus === REQUESTING ||
        aptsSlotOverrideStatus === REQUESTING
      }
      confirmationOpts={confirmationOpts}
    >
      {!!errorText && <ErrorView style={styles.errorView} errorText={errorText} />}
      {!noPermissionForSelectedEventType && (
        <View style={[__WEB__ ? styles.content : null, errorText ? styles.contentWithError : null]}>
          <View style={__WEB__ ? styles.mainRow : null}>
            <Panel title={calendarTitle}>
              <View style={__WEB__ ? styles.calendarContainer : null}>
                <Cal
                  schedulingForCaseId={appointmentDetails?.caseId}
                  openCaseInfo={(caseId) => dispatch(openCaseInfo(caseId))}
                  onSelectedDate={setSelectedDate}
                  events={events}
                  eventType={selectedEventType}
                  slotAvailability={aptsSlotAvailabilityData}
                  aptsSlotAvailabilityStatus={aptsSlotAvailabilityStatus}
                  eventsByStartDate={eventsByStartDate}
                  disablePastSelect={false}
                  canEdit={canEditSelectedEventType}
                  aptsRequestStatus={aptsRequestStatus}
                />
              </View>
            </Panel>
            {hasAppointmentRequests ? (
              <Panel
                title={`${selectedEventLabel} Appointment Requests`}
                containerStyle={styles.flexGrow}
                collapsable={!__WEB__}
                extraHeaderContent={
                  <HeaderActions
                    showExportButton={showExportButton}
                    showAgentDemoButton={showAgentDemoButton}
                    onExport={handleExportPress}
                    onAgentDemo={handleBulkCallPress}
                  />
                }
              >
                <RequestedAppointments
                  employer={employer}
                  eventType={selectedEventType}
                  canEdit={canEditSelectedEventType}
                />
              </Panel>
            ) : (
              <Panel
                title={`${todaysDateLabel} - ${appointmentsTitle}`}
                containerStyle={styles.flexGrow}
              >
                <ScheduledAppointments
                  selectedDate={selectedDate}
                  eventsByTime={eventsByTime}
                  overrides={overrides}
                  onCancelPress={handleCancelEventPress}
                  onInfoPress={handleEventInfoPress}
                  onContactPress={handleScheduledEventContactPress}
                  onRevenuePress={handleScheduledEventRevenuePress}
                  canEdit={canEditSelectedEventType}
                  eventType={selectedEventType}
                />
              </Panel>
            )}
          </View>
          {!!hasAppointmentRequests && (
            <Panel
              title={`${todaysDateLabel} - ${appointmentsTitle}`}
              containerStyle={styles.flexGrow}
            >
              <ScheduledAppointments
                selectedDate={selectedDate}
                eventsByTime={eventsByTime}
                overrides={overrides}
                onCancelPress={handleCancelEventPress}
                onInfoPress={handleEventInfoPress}
                onContactPress={handleScheduledEventContactPress}
                onRevenuePress={handleScheduledEventRevenuePress}
                canEdit={canEditSelectedEventType}
                eventType={selectedEventType}
              />
            </Panel>
          )}
          {shouldRenderCancelledAppointments && (
            <Panel
              title={` ${selectedEventLabel} Cancellation Alerts`}
              containerStyle={styles.flexGrow}
            >
              <CancelledAppointments
                employer={employer}
                eventType={selectedEventType}
                onCancelPress={handleCancelEventPress}
                onCancelledApptNotification={handleCancelledNotification}
              />
            </Panel>
          )}
        </View>
      )}
    </Container>
  );
});

const styles = StyleSheet.create({
  container: { flex: 1 },
  appointmentsNav: {
    backgroundColor: 'rgb(244,244,244)',
    height: 50,
    justifyContent: 'center',
    alignItems: 'stretch',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'rgb(228,228,228)',
  },
  appointmentsNavWebContent: {
    maxWidth: '100%',
    overflowX: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
  },
  appointmentsNavScroll: {
    height: '100%',
  },
  appointmentsNavScrollContent: {
    paddingLeft: 10,
    paddingTop: 12,
  },
  apptNavBtn: {
    paddingHorizontal: 20,
    height: 25,
    justifyContent: 'center',
  },
  apptNavBtnSelected: {
    borderWidth: 1.5,
    borderColor: COLOR.emphasis,
    borderRadius: 25,
    backgroundColor: 'white',
  },
  apptNavBtnText: {
    ...FONT.regular,
    color: 'rgb(86,86,86)',
  },
  apptNavBtnTextSelected: {
    ...FONT.medium,
    color: 'rgb(54,54,54)',
  },
  content: {
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 20,
  },
  contentWithError: {
    paddingTop: 0,
  },
  mainRow: {
    flexDirection: 'row',
  },
  calendarContainer: {
    width: 550,
  },
  flexGrow: {
    flexGrow: 1,
  },
  errorView: {
    marginLeft: 4,
    marginTop: 10,
  },
  headerActions: {
    flexDirection: 'row',
    marginRight: 15,
    gap: 10,
  },
  exportButton: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    height: 40,
    width: 130,
    backgroundColor: COLOR.button,
    borderRadius: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  exportIcon: {
    width: 10,
    height: 12,
    tintColor: 'white',
    marginRight: 10,
  },
  exportText: {
    ...FONT.regular,
    fontSize: 14,
    color: 'white',
    textAlign: 'center',
  },
  bulkCallButton: {
    paddingVertical: 8,
    paddingHorizontal: 15,
    height: 40,
    backgroundColor: COLOR.emphasis,
    borderRadius: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 15,
  },
  bulkCallText: {
    ...FONT.regular,
    fontSize: 14,
    color: 'white',
    textAlign: 'center',
  },
  cmrSwitchRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cmrMobileLabel: {
    fontSize: 14,
  },
  cmrLeftLabel: {
    marginRight: __WEB__ ? 15 : 7,
  },
  cmrRightLabel: {
    marginLeft: __WEB__ ? 15 : 18,
  },
});

export default PHAAdmin;
